import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { sendStatusWA } from '../../../lib/fonnte';
import { uploadToCloudinary } from '../../../lib/cloudinary';
import { getLoyaltySettings, calcEarnPoints, recordLoyaltyPoints } from '../../../lib/loyalty';

const STATUS_FLOW = [
  { value: 'open',     label: 'Buka',          color: '#1B5E20', bg: '#E8F5E9' },
  { value: 'closed',   label: 'Ditutup',        color: '#E65100', bg: '#FFF3E0' },
  { value: 'diproses', label: 'Diproses',       color: '#0D47A1', bg: '#E3F2FD' },
  { value: 'dikirim',  label: 'Dalam Pengiriman', color: '#4A148C', bg: '#F3E5F5' },
  { value: 'ditempat', label: 'Di Lokasi COD',  color: '#2E7D32', bg: '#E8F5E9' },
  { value: 'selesai',  label: 'Selesai',        color: '#555',    bg: '#F5F5F5' },
  { value: 'batal',    label: 'Batal',          color: '#B71C1C', bg: '#FFEBEE' },
];

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
}

const emptyForm = { product_id: '', area_id: '', date: '', time_start: '', time_end: '', min_packs: 20, promo_image_url: '', notes: '' };

export default function AdminPO() {
  const [pos, setPOs] = useState([]);
  const [areas, setAreas] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sendingWA, setSendingWA] = useState({});
  const [originalImageUrl, setOriginalImageUrl] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    const [posRes, areasRes, productsRes] = await Promise.all([
      supabase.from('po_batches').select('*, product:products(*), area:areas(*), po_orders(id,qty,status,payment_method,payment_status,customer_id,customer:customers(name,wa))').order('date', { ascending: false }),
      supabase.from('areas').select('*').order('name'),
      supabase.from('products').select('*').eq('active', true).order('name'),
    ]);
    setPOs(posRes.data || []);
    setAreas(areasRes.data || []);
    setProducts(productsRes.data || []);
  }

  async function handleSave() {
    if (!form.product_id || !form.area_id || !form.date || !form.time_start || !form.time_end) return;
    if (editId) {
      await supabase.from('po_batches').update(form).eq('id', editId);
    } else {
      await supabase.from('po_batches').insert(form);
    }
    setForm(emptyForm); setEditId(null); setShowForm(false); load();
  }

  async function handleStatusChange(po, newStatus) {
    await supabase.from('po_batches').update({ status: newStatus }).eq('id', po.id);

    // Jika batch selesai → update semua po_orders (bukan batal) jadi confirmed
    // lalu berikan poin loyalty ke masing-masing customer
    if (newStatus === 'selesai') {
      // Ambil semua order yang akan di-confirm (pending/confirmed, bukan batal)
      const { data: ordersToConfirm } = await supabase
        .from('po_orders')
        .select('id, customer_id, total, customers(visit_count)')
        .eq('po_id', po.id)
        .neq('status', 'batal');

      // Update status → confirmed
      await supabase
        .from('po_orders')
        .update({ status: 'confirmed' })
        .eq('po_id', po.id)
        .neq('status', 'batal');

      // Catat poin loyalty per customer (non-fatal)
      try {
        const loySettings = await getLoyaltySettings();
        if (loySettings.enabled && ordersToConfirm?.length > 0) {
          for (const order of ordersToConfirm) {
            if (!order.customer_id) continue;
            const earnPts      = calcEarnPoints(order.total, loySettings, new Date(), false); // double point tidak berlaku untuk PO
            const isNewCustomer = (order.customers?.visit_count || 0) <= 1;
            await recordLoyaltyPoints({
              customerId:    order.customer_id,
              orderId:       order.id,
              orderType:     'po',
              earnPoints:    earnPts,
              isNewCustomer,
              welcomeBonus:  loySettings.welcomeBonus,
            });
          }
        }
      } catch (e) {
        console.warn('Loyalty points error (non-fatal):', e);
      }
    }

    // Trigger WA ke semua customer dalam PO ini
    const statusesWithWA = ['diproses', 'dikirim', 'ditempat', 'selesai', 'batal'];
    if (statusesWithWA.includes(newStatus)) {
      setSendingWA(s => ({ ...s, [po.id]: true }));
      const updatedPO = { ...po, status: newStatus };
      const orderCustomers = (po.po_orders || []).map(o => ({
        customer_wa:    o.customer?.wa,
        customer_name:  o.customer?.name,
        payment_method: o.payment_method,
        payment_status: o.payment_status,
      })).filter(o => o.customer_wa);
      await sendStatusWA(orderCustomers, updatedPO, po.product, po.area);
      setSendingWA(s => ({ ...s, [po.id]: false }));
    }
    load();
  }

  async function handleImageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadToCloudinary(file, 'sapan/promo');
      setForm(f => ({ ...f, promo_image_url: url }));
    } catch (err) {
      alert('Upload gagal, coba lagi.');
    }
    setUploading(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Hapus PO ini? Foto promo di Cloudinary perlu dihapus manual.')) return;
    await supabase.from('po_batches').delete().eq('id', id);
    load();
  }

  function handleCancelForm() {
    setShowForm(false);
    setForm(emptyForm);
    setEditId(null);
    setOriginalImageUrl('');
  }

  const getTotals = (po) => {
    const valid = (po.po_orders || []).filter(o =>
      o.status !== 'batal' &&
      (o.payment_method === 'cod' || (o.payment_method === 'non-tunai' && o.payment_status === 'paid'))
    );
    return { packs: valid.reduce((s, o) => s + o.qty, 0), count: valid.length };
  };

  const imageChanged = editId && form.promo_image_url && form.promo_image_url !== originalImageUrl;

  return (
    <div style={{ padding: 'clamp(16px,4vw,28px) clamp(12px,4vw,24px) 60px' }}>
      <style>{`@media(max-width:640px){.po-form-grid{grid-template-columns:1fr!important}}`}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
        <div>
          <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.5rem' }}>Kelola PO</div>
          <div style={{ fontSize: '.82rem', color: 'var(--muted)' }}>{pos.length} PO terdaftar</div>
        </div>
        <button onClick={() => { setForm(emptyForm); setEditId(null); setOriginalImageUrl(''); setShowForm(true); }}
          style={{ padding: '9px 18px', borderRadius: 8, background: '#CD5226', color: '#fff', border: 'none', fontSize: '.84rem', fontFamily: "'DM Sans',sans-serif" }}>
          + Buat PO Baru
        </button>
      </div>

      {showForm && (
        <div className="fade-up" style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '22px', marginBottom: 20, boxShadow: 'var(--shadow)' }}>
          <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.1rem', marginBottom: 18 }}>{editId ? 'Edit PO' : 'Buat PO Baru'}</div>
          <div className="po-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            {[
              ['Produk *', 'product_id', 'select', products.map(p => ({ v: p.id, l: `${p.name} — Rp ${p.price.toLocaleString('id-ID')}` }))],
              ['Area *', 'area_id', 'select', areas.map(a => ({ v: a.id, l: a.name }))],
              ['Tanggal *', 'date', 'date', null],
              ['Minimum Pack *', 'min_packs', 'number', null],
              ['Jam Mulai *', 'time_start', 'time', null],
              ['Jam Selesai *', 'time_end', 'time', null],
            ].map(([label, field, type, opts]) => (
              <div key={field}>
                <label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 4 }}>{label}</label>
                {type === 'select' ? (
                  <select value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                    style={{ width: '100%', border: '1.5px solid #DDD', borderRadius: 8, padding: '8px 12px', fontSize: '.86rem', outline: 'none' }}>
                    <option value="">Pilih...</option>
                    {(opts || []).map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                ) : (
                  <input type={type} value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                    style={{ width: '100%', border: '1.5px solid #DDD', borderRadius: 8, padding: '8px 12px', fontSize: '.86rem', outline: 'none' }} />
                )}
              </div>
            ))}
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 6 }}>Foto Promosi</label>

            {editId && originalImageUrl && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: '.72rem', color: 'var(--muted)', marginBottom: 4 }}>Foto saat ini:</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <img src={originalImageUrl} alt="" style={{ width: 120, height: 72, objectFit: 'cover', borderRadius: 6, border: '2px solid #DDD' }} />
                  {imageChanged && <span style={{ fontSize: '.72rem', color: '#27AE60', background: '#E8F5E9', padding: '2px 8px', borderRadius: 20, border: '1px solid #A5D6A7' }}>✓ Akan diganti</span>}
                </div>
              </div>
            )}

            {imageChanged && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: '.72rem', color: 'var(--muted)', marginBottom: 4 }}>Foto baru:</div>
                <img src={form.promo_image_url} alt="" style={{ width: 120, height: 72, objectFit: 'cover', borderRadius: 6, border: '2px solid #27AE60' }} />
              </div>
            )}

            {!editId && form.promo_image_url && (
              <div style={{ marginBottom: 8 }}>
                <img src={form.promo_image_url} alt="" style={{ width: 120, height: 72, objectFit: 'cover', borderRadius: 6, border: '2px solid #DDD' }} />
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 7, background: 'var(--cream)', border: '1.5px solid var(--border)', fontSize: '.78rem', cursor: 'pointer', color: 'var(--dark)' }}>
                {editId && originalImageUrl ? '🔄 Ganti Foto' : '📷 Pilih Foto'}
                <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
              </label>
              {uploading && <span style={{ fontSize: '.76rem', color: 'var(--muted)' }}>Mengunggah...</span>}
              {imageChanged && (
                <button type="button" onClick={() => setForm(f => ({ ...f, promo_image_url: originalImageUrl }))}
                  style={{ fontSize: '.72rem', color: '#C62828', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  Batalkan ganti
                </button>
              )}
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 4 }}>Catatan</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              style={{ width: '100%', border: '1.5px solid #DDD', borderRadius: 8, padding: '8px 12px', fontSize: '.86rem', outline: 'none', minHeight: 70, resize: 'vertical', fontFamily: "'DM Sans',sans-serif" }} />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={handleCancelForm}
              style={{ padding: '8px 16px', borderRadius: 8, background: 'var(--cream)', border: '1px solid var(--border)', color: 'var(--muted)', fontSize: '.82rem', fontFamily: "'DM Sans',sans-serif" }}>Batal</button>
            <button onClick={handleSave}
              style={{ padding: '8px 18px', borderRadius: 8, background: '#CD5226', color: '#fff', border: 'none', fontSize: '.82rem', fontFamily: "'DM Sans',sans-serif" }}>
              {editId ? 'Simpan Perubahan' : 'Buat PO'}
            </button>
          </div>
        </div>
      )}

      {pos.map(po => {
        const { packs, count } = getTotals(po);
        const pct = Math.min(100, Math.round((packs / (po.min_packs || 1)) * 100));
        const st = STATUS_FLOW.find(s => s.value === po.status) || STATUS_FLOW[0];
        return (
          <div key={po.id} style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '18px 20px', marginBottom: 12, boxShadow: 'var(--shadow)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.05rem' }}>{po.product?.name}</div>
                <div style={{ fontSize: '.78rem', color: 'var(--muted)', marginTop: 1 }}>Rp {(po.product?.price || 0).toLocaleString('id-ID')}/pack · {po.product?.isi}</div>
              </div>
              <span style={{ background: st.bg, color: st.color, borderRadius: 20, padding: '3px 10px', fontSize: '.66rem', fontFamily: "'DM Mono',monospace", letterSpacing: '.8px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{st.label}</span>
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
              {[`📍 ${po.area?.name}`, `📅 ${fmtDate(po.date)}`, `🕐 ${po.time_start?.slice(0,5)}–${po.time_end?.slice(0,5)}`, `📦 Min. ${po.min_packs} pack`].map(t => (
                <span key={t} style={{ fontSize: '.75rem', color: 'var(--muted)' }}>{t}</span>
              ))}
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 4 }}>
                <span>Order: <strong style={{ color: 'var(--dark)' }}>{packs} pack</strong> dari {count} customer</span>
                <span>{pct}% dari kuota {po.min_packs}</span>
              </div>
              <div style={{ background: '#EEE', borderRadius: 50, height: 6, overflow: 'hidden' }}>
                <div style={{ width: pct + '%', height: '100%', borderRadius: 50, background: pct >= 100 ? '#27AE60' : 'linear-gradient(90deg,var(--gold),var(--gold-light))', transition: 'width .5s' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <select value={po.status} onChange={e => handleStatusChange(po, e.target.value)}
                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--cream)', fontSize: '.76rem', fontFamily: "'DM Mono',monospace" }}>
                {STATUS_FLOW.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              {sendingWA[po.id] && <span style={{ fontSize: '.74rem', color: '#1B5E20' }}>📱 Mengirim WA...</span>}
              <button onClick={() => {
                const imgUrl = po.promo_image_url || '';
                setForm({ product_id: po.product_id, area_id: po.area_id, date: po.date, time_start: po.time_start, time_end: po.time_end, min_packs: po.min_packs, promo_image_url: imgUrl, notes: po.notes || '' });
                setOriginalImageUrl(imgUrl);
                setEditId(po.id);
                setShowForm(true);
                window.scrollTo(0, 0);
              }}
                style={{ padding: '5px 12px', borderRadius: 6, background: 'var(--cream)', border: '1px solid var(--border)', fontSize: '.74rem', fontFamily: "'DM Sans',sans-serif" }}>Edit</button>
              <button onClick={() => handleDelete(po.id)}
                style={{ padding: '5px 12px', borderRadius: 6, background: '#FFEBEE', border: '1px solid #FFCDD2', color: '#C62828', fontSize: '.74rem', fontFamily: "'DM Sans',sans-serif" }}>Hapus</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
